// src/statsUtils.js

import { getCreatedDate, getCreatedTimeMsFromCreated } from "./utils/dateUtils";
import { isTapeReading } from './utils/tradeHelpers';

// Get daily PnL for a given year and month
export function getDailyPnl(entries, year, month) {
  const byDay = {};
  // Only count actual trades (not deposits, payouts, tape reading, or reset-excluded)
  entries.filter(e => 
    String(e.year) === String(year) && 
    String(e.month) === String(month) &&
    !e.isDeposit && 
    !e.isPayout && 
    !isTapeReading(e) &&
    !e.isResetExcluded &&
    e.pnl !== undefined &&
    e.pnl !== null &&
    e.pnl !== ""
  ).forEach(entry => {
    let d = null;
    if (entry.year && entry.month && entry.day) {
      d = new Date(parseInt(entry.year, 10), parseInt(entry.month, 10) - 1, parseInt(entry.day, 10));
    } else if (entry.created) {
      d = getCreatedDate(entry.created);
    }
    const dayKey = d && !isNaN(d.getTime()) ? d.toLocaleDateString() : `${entry.month}/${entry.day}/${entry.year}`;
    const pnlValue = Number(entry.pnl) || 0;
    byDay[dayKey] = Math.round(((byDay[dayKey] || 0) + pnlValue) * 100) / 100;
  });
  return byDay;
}

// Get weekly PnL for a given year and month, using a calendar grid
export function getWeeklyPnls(entries, year, month, calendarGrid) {
  // Only entries in the given month that are actual trades
  const filtered = entries.filter(e => 
    String(e.year) === String(year) && 
    String(e.month) === String(month) &&
    !e.isDeposit && 
    !e.isPayout && 
    !isTapeReading(e) &&
    !e.isResetExcluded &&
    e.pnl !== undefined &&
    e.pnl !== null &&
    e.pnl !== ""
  );
  // Build a map day->PnL and day->count
  const dayPnls = {};
  const dayCounts = {};
  filtered.forEach(e => {
    const day = parseInt(e.day, 10);
    if (!isNaN(day)) {
      const pnlValue = Number(e.pnl) || 0;
      dayPnls[day] = Math.round(((dayPnls[day] || 0) + pnlValue) * 100) / 100;
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    }
  });
  // For each week in the grid, sum the PnL and count for the days in that week
  return calendarGrid.map(weekArr => {
    let sum = 0;
    let count = 0;
    weekArr.forEach(day => {
      if (day && Object.prototype.hasOwnProperty.call(dayPnls, day)) {
        sum += dayPnls[day];
        count += dayCounts[day] || 0;
      }
    });
    return { pnl: Math.round(sum * 100) / 100, count };
  });
}

// Get monthly PnL for a given year and month
export function getMonthlyPnl(entries, year, month) {
  const result = entries.filter(e => 
    String(e.year) === String(year) && 
    String(e.month) === String(month) &&
    !e.isDeposit && 
    !e.isPayout && 
    !isTapeReading(e) &&
    !e.isResetExcluded &&
    e.pnl !== undefined &&
    e.pnl !== null &&
    e.pnl !== ""
  ).reduce((sum, e) => sum + (Number(e.pnl) || 0), 0);
  return Math.round(result * 100) / 100;
}

// Precision-safe sum function
function sumPrecise(arr) {
  return arr.reduce((sum, v) => sum + Math.round(Number(v) * 100), 0) / 100;
}

function isActualTradeEntry(entry) {
  return !entry.isDeposit && !entry.isPayout && !isTapeReading(entry) && !entry.isResetExcluded && entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== "";
}

export function getBalanceBeforeFirstMatchingTrade(entries, isMatchingTrade) {
  const sortedEntries = [...entries].sort((a, b) => {
    const aTime = getCreatedTimeMsFromCreated(a.created);
    const bTime = getCreatedTimeMsFromCreated(b.created);
    return aTime - bTime;
  });
  let runningCapital = 0;
  let capitalBeforeFirstTrade = null;

  sortedEntries.forEach((entry) => {
    if (capitalBeforeFirstTrade === null && isActualTradeEntry(entry) && isMatchingTrade(entry)) {
      capitalBeforeFirstTrade = runningCapital;
    }

    if (entry.isBalanceAdjustment) {
      // Balance adjustments set the balance to their target (same rule as accountBalanceUtils).
      const target = Number(entry.adjustmentTarget ?? entry.accountBalance);
      runningCapital = Number.isFinite(target) ? target : runningCapital + (Number(entry.pnl) || 0);
    } else if (entry.isDeposit) {
      runningCapital += Number(entry.pnl) || 0;
    } else if (entry.isPayout) {
      runningCapital -= Math.abs(Number(entry.pnl) || 0);
    } else if (isActualTradeEntry(entry)) {
      runningCapital += Number(entry.pnl) || 0;
    }
  });

  return capitalBeforeFirstTrade === null
    ? null
    : Math.round(capitalBeforeFirstTrade * 100) / 100;
}

export const DEFAULT_BE_THRESHOLD = 20;
const MIN_BE_THRESHOLD = 1;
const MAX_BE_THRESHOLD = 1000;

export function sanitizeBeThreshold(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_BE_THRESHOLD;
  if (parsed < MIN_BE_THRESHOLD) return MIN_BE_THRESHOLD;
  if (parsed > MAX_BE_THRESHOLD) return MAX_BE_THRESHOLD;
  return Math.round(parsed);
}

export function classifyTradeOutcomeByThreshold(pnlValue, beThreshold = DEFAULT_BE_THRESHOLD) {
  const pnl = Number(pnlValue) || 0;
  const threshold = sanitizeBeThreshold(beThreshold);
  const absPnl = Math.abs(pnl);

  // Keep BE classification aligned with calendar coloring logic (+/- threshold).
  if (pnl === 0 || (absPnl >= 1 && absPnl <= threshold)) return "breakeven";
  if (pnl > 0) return "win";
  if (pnl < 0) return "loss";
  return "breakeven";
}

// Calculate trading performance percentages (fixed logic from SummaryPage)
export function getTradingPerformance(entries, targetYear = null, targetMonth = null) {
  if (!entries.length) return null;
  
  // Sort all entries chronologically
  const sortedEntries = [...entries].sort((a, b) => {
    const aTime = getCreatedTimeMsFromCreated(a.created);
    const bTime = getCreatedTimeMsFromCreated(b.created);
    return aTime - bTime;
  });
  
  // Helper to get entry date - unused for now
  // const getEntryDate = (entry) => {
  //   if (entry.year && entry.month && entry.day) {
  //     return new Date(parseInt(entry.year), parseInt(entry.month) - 1, parseInt(entry.day));
  //   }
  //   if (entry.created) {
  //     return new Date(entry.created.split('-')[0]);
  //   }
  //   return new Date();
  // };
  
  // Calculate the starting capital (sum of all deposits minus payouts)
  const totalDeposits = sumPrecise(
    sortedEntries
      .filter(e => e.isDeposit)
      .map(e => Number(e.pnl) || 0)
  );
  
  const totalPayouts = sumPrecise(
    sortedEntries
      .filter(e => e.isPayout)
      .map(e => Number(e.pnl) || 0) // Already negative
  );
  
  // Handle different starting capital scenarios
  let originalCapital = totalDeposits;
  
  if (totalDeposits === 0 && totalPayouts === 0) {
    // Scenario 1: Brand new funded account (starting balance: $0)
    originalCapital = 0;
  } else if (totalDeposits === 0 && totalPayouts !== 0) {
    // Scenario 2: No deposits but payouts exist (manual balance edits)
    originalCapital = Math.abs(totalPayouts);
  }

  // Fallback when funding logs are missing but trades include account balance.
  const firstTrade = sortedEntries.find(isActualTradeEntry);
  let inferredStartingCapital = 0;
  if (firstTrade && firstTrade.accountBalance !== undefined && firstTrade.accountBalance !== null && firstTrade.accountBalance !== "") {
    const firstBalance = Number(firstTrade.accountBalance) || 0;
    const firstPnl = Number(firstTrade.pnl) || 0;
    inferredStartingCapital = firstBalance - firstPnl;
  }
  
  // If specific year/month requested, filter trades to that period
  let tradesToAnalyze = sortedEntries.filter(isActualTradeEntry);
  let capitalBase = originalCapital;
  
  if (targetYear !== null && targetMonth !== null) {
    tradesToAnalyze = tradesToAnalyze.filter(e => 
      String(e.year) === String(targetYear) && String(e.month) === String(targetMonth)
    );

    // For period calculations, use capital right before the first trade in that period.
    const periodCapital = getBalanceBeforeFirstMatchingTrade(
      sortedEntries,
      (entry) => String(entry.year) === String(targetYear) && String(entry.month) === String(targetMonth)
    );
    if (periodCapital !== null && periodCapital > 0) {
      capitalBase = periodCapital;
    } else if (capitalBase <= 0 && inferredStartingCapital > 0) {
      capitalBase = inferredStartingCapital;
    }
  } else if (capitalBase <= 0 && inferredStartingCapital > 0) {
    capitalBase = inferredStartingCapital;
  }
  
  // Accounts funded by a balance adjustment (no deposit rows) still have a real starting
  // balance in the ledger: use the balance right before the first trade.
  if (capitalBase <= 0) {
    const ledgerCapital = getBalanceBeforeFirstMatchingTrade(sortedEntries, () => true);
    if (ledgerCapital !== null && ledgerCapital > 0) capitalBase = ledgerCapital;
  }

  // Calculate total trading P&L for the period
  const tradingPnl = sumPrecise(tradesToAnalyze.map(e => Number(e.pnl) || 0));
  
  // Calculate performance percentage based on original capital
  // For brand new accounts (starting with $0), show high percentage for profitable periods
  const performancePercentage = capitalBase > 0 ? (tradingPnl / capitalBase) * 100 : (tradingPnl > 0 ? 999999 : 0);
  
  return {
    pnl: tradingPnl,
    percentage: performancePercentage,
    startingCapital: capitalBase,
    progressToward20Percent: Math.min(100, Math.max(0, (performancePercentage / 20) * 100)),
    hasTrades: tradesToAnalyze.length > 0
  };
}

// Get the current account balance using the logic from SummaryPage
export function getCurrentBalance(entries) {
  if (!entries.length) return { balance: '0.00', totalDeposits: '0.00', totalPayouts: '0.00' };
  const sorted = [...entries].sort((a, b) => new Date(a.created) - new Date(b.created));
  let bal = 0;
  let totalDeposits = 0;
  let totalPayouts = 0;
  for (let i = 0; i < sorted.length; i++) {
    const e = sorted[i];
    if (e.isBalanceAdjustment) {
      const targetBalance = Number(e.adjustmentTarget ?? e.accountBalance);
      bal = Number.isFinite(targetBalance) ? targetBalance : bal + (Number(e.pnl) || 0);
    } else if (e.isDeposit) {
      // For deposits, add the deposit amount to the current balance
      const depositAmount = Number(e.pnl) || 0;
      bal += depositAmount;
      totalDeposits += depositAmount;
    } else if (e.isPayout) {
      // For payouts, add the payout amount (pnl is stored as negative)
      const payoutAmount = Number(e.pnl) || 0; // Already negative
      bal += payoutAmount;
      totalPayouts += Math.abs(payoutAmount);
    } else if (!isTapeReading(e) && !e.isResetExcluded) {
      // For trades, add the P&L to the previous balance (exclude reset-excluded trades)
      bal += Number(e.pnl) || 0;
    }
    // Tape reading entries don't affect balance
  }
  return {
    balance: (Math.round(bal * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    totalDeposits: (Math.round(totalDeposits * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    totalPayouts: (Math.round(totalPayouts * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })
  };
}

// --- Shared analytics helpers (Summary + Visualization) ---

function getEntryDateFromEntry(entry) {
  if (entry.year != null && entry.month != null && entry.day != null) {
    const y = parseInt(entry.year, 10);
    const m = parseInt(entry.month, 10) - 1;
    const d = parseInt(entry.day, 10);
    const date = new Date(y, m, d);
    return isNaN(date.getTime()) ? null : date;
  }
  if (entry.created) return getCreatedDate(entry.created);
  return null;
}

/** Expectancy: E = P(win)*AvgWin - P(loss)*|AvgLoss|. trades = array of trade entries with pnl. */
export function computeExpectancy(trades) {
  if (!trades || !trades.length) return 0;
  const wins = trades.filter(e => Number(e.pnl) > 0);
  const losses = trades.filter(e => Number(e.pnl) < 0);
  const n = trades.length;
  const pWin = wins.length / n;
  const pLoss = losses.length / n;
  const avgWin = wins.length ? sumPrecise(wins.map(e => e.pnl)) / wins.length : 0;
  const avgLoss = losses.length ? sumPrecise(losses.map(e => e.pnl)) / losses.length : 0;
  return Math.round((pWin * avgWin + pLoss * avgLoss) * 100) / 100;
}

/** Profit factor: sum(wins) / |sum(losses)|. Returns null if no losses. */
export function computeProfitFactor(trades) {
  if (!trades || !trades.length) return null;
  const sumWins = sumPrecise(trades.filter(e => Number(e.pnl) > 0).map(e => e.pnl));
  const sumLosses = sumPrecise(trades.filter(e => Number(e.pnl) < 0).map(e => e.pnl));
  if (sumLosses >= 0) return sumWins > 0 ? null : 0;
  const pf = sumWins / Math.abs(sumLosses);
  return Math.round(pf * 100) / 100;
}

/** Max drawdown from equity curve points. points: [{ y, type? }]. Optionally include non-Trade points. */
export function computeMaxDrawdown(points, { includeDepositsPayouts = false } = {}) {
  if (!points || points.length < 2) return { maxDrawdown: 0, maxDrawdownPct: 0 };
  const filtered = includeDepositsPayouts ? points : points.filter(p => p.type === 'Trade');
  if (filtered.length < 2) return { maxDrawdown: 0, maxDrawdownPct: 0 };
  let peak = filtered[0].y;
  let maxDd = 0;
  let maxDdPct = 0;
  for (let i = 1; i < filtered.length; i++) {
    const y = filtered[i].y;
    if (y > peak) peak = y;
    const dd = peak - y;
    if (dd > maxDd) maxDd = dd;
    if (peak > 0 && peak !== y) {
      const pct = (dd / peak) * 100;
      if (pct > maxDdPct) maxDdPct = pct;
    }
  }
  return { maxDrawdown: Math.round(maxDd * 100) / 100, maxDrawdownPct: Math.round(maxDdPct * 100) / 100 };
}

/** Daily PnL series: array of { dateKey, pnl } for each day that has trades. */
export function computeDailyPnlSeries(trades) {
  if (!trades || !trades.length) return [];
  const byDay = {};
  const toLocalDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  trades.forEach(e => {
    const d = getEntryDateFromEntry(e);
    if (!d || isNaN(d.getTime())) return;
    const key = toLocalDateKey(d);
    byDay[key] = (byDay[key] || 0) + (Number(e.pnl) || 0);
  });
  return Object.entries(byDay).map(([dateKey, pnl]) => ({ dateKey, pnl: Math.round(pnl * 100) / 100 })).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

/** Percent of days with positive PnL. */
export function computeGreenDayPercent(dailySeries) {
  if (!dailySeries || !dailySeries.length) return 0;
  const green = dailySeries.filter(d => d.pnl > 0).length;
  return Math.round((green / dailySeries.length) * 1000) / 10;
}

export function median(values) {
  if (!values || !values.length) return 0;
  const sorted = [...values].filter(v => typeof v === 'number' && !Number.isNaN(v)).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Group trades by day of week. Returns { dayName: trades[] }. */
export function groupByDayOfWeek(trades) {
  const groups = {};
  DAY_NAMES.forEach(d => { groups[d] = []; });
  (trades || []).forEach(e => {
    const d = getEntryDateFromEntry(e);
    if (!d) return;
    const name = DAY_NAMES[d.getDay()];
    if (!groups[name]) groups[name] = [];
    groups[name].push(e);
  });
  return groups;
}

/** Group trades by tradeSession. Returns { sessionLabel: trades[] }. */
export function groupBySession(trades) {
  const groups = {};
  (trades || []).forEach(e => {
    const session = e.tradeSession || 'Unknown';
    if (!groups[session]) groups[session] = [];
    groups[session].push(e);
  });
  return groups;
}

/** Parse entryTime "HH:MM" or "H:MM" to hour (0-23). Returns null if missing/invalid. */
function getEntryHour(entry) {
  const t = entry.entryTime;
  if (!t || typeof t !== 'string') return null;
  const parts = t.trim().split(':');
  const h = parseInt(parts[0], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  return h;
}

/** Group trades by entry hour bucket. Returns { hourLabel: trades[] } e.g. "9", "10", "14". */
export function groupByEntryHour(trades) {
  const groups = {};
  (trades || []).forEach(e => {
    const h = getEntryHour(e);
    const label = h === null ? 'Unknown' : String(h);
    if (!groups[label]) groups[label] = [];
    groups[label].push(e);
  });
  return groups;
}

/** Normalize trade-env field value for grouping: Yes / No / N/A, or Aligned / Not Aligned / N/A for aboveBelow. */
function normalizeTradeEnvValue(value, fieldDef) {
  if (value === undefined || value === null || value === '') return 'N/A';
  if (fieldDef.id === 'aboveBelow0000' || fieldDef.id === 'aboveBelow0830') {
    if (value === true || value === 'above' || value === 'Aligned') return 'Aligned';
    if (value === false || value === 'below' || value === 'Not Aligned') return 'Not Aligned';
    return 'N/A';
  }
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return String(value);
}

/** Group trades by a trade-env field. fieldDef: { id, label, fieldType, options }. Returns { valueLabel: trades[] }. */
export function groupByTradeEnvField(trades, fieldDef) {
  if (!fieldDef || !fieldDef.id) return {};
  const groups = {};
  (trades || []).forEach(e => {
    const raw = e[fieldDef.id];
    const label = normalizeTradeEnvValue(raw, fieldDef);
    if (!groups[label]) groups[label] = [];
    groups[label].push(e);
  });
  return groups;
}

/** Win/loss stats for trades; winRate excludes break-even trades from denominator. */
export function computeWinLossStats(trades, beThreshold = DEFAULT_BE_THRESHOLD) {
  let wins = 0, losses = 0, breakeven = 0, sum = 0;
  (trades || []).forEach(e => {
    const pnl = Number(e.pnl) || 0;
    sum += pnl;
    const outcome = classifyTradeOutcomeByThreshold(pnl, beThreshold);
    if (outcome === "win") wins++;
    else if (outcome === "loss") losses++;
    else breakeven++;
  });
  const total = wins + losses + breakeven;
  const decisiveTrades = wins + losses;
  const winRate = decisiveTrades ? (wins / decisiveTrades) * 100 : 0;
  const avgPnl = total ? sum / total : 0;
  return {
    wins,
    losses,
    breakeven,
    total,
    winRate,
    avgPnl: Math.round(avgPnl * 100) / 100,
    sum: Math.round(sum * 100) / 100,
  };
}
